<script setup>
import { Head, Link } from "@inertiajs/inertia-vue3";
import Layout from "../../../Layouts/ManagementLayout.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Tag from "primevue/tag";
import Dropdown from "primevue/dropdown";
import { ref, watch, computed, onMounted } from "vue";
import { Inertia } from "@inertiajs/inertia";
import { usePage } from "@inertiajs/inertia-vue3";
import { debounce } from "../../../utils/debounce";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import ManagementHeader from "../../../Components/Management/ManagementHeader.vue";
import ProviderEditForm from "../../../Components/Management/ProviderEditForm.vue";

const props = defineProps({
    providers:       { type: Object, default: () => ({ data: [], links: [], from: 0, to: 0, total: 0 }) },
    companies:       { type: Array,  default: () => [] },
    services:        { type: Array,  default: () => [] },
    filters:         { type: Object, default: () => ({ search: '', company_id: '' }) },
    flash:           { type: Object, default: null },
    editingProvider: { type: Object, default: null },
});

const page    = usePage();
const confirm = useConfirm();
const toast   = useToast();
const search    = ref(props.filters?.search ?? '');
const companyId = ref(props.filters?.company_id ?? '');

const isSuperAdmin = computed(() => page.props.value?.auth?.user?.role === 'super_admin');

// ── Mode state ─────────────────────────────────────────────────────────
const mode = ref('list');  // 'list' | 'edit'

onMounted(() => {
    if (props.editingProvider) mode.value = 'edit';
});

watch(() => props.editingProvider, (val) => {
    mode.value = val ? 'edit' : 'list';
});

// ── List search / filter ───────────────────────────────────────────────
const updateSearch = debounce(() => {
    const params = {
        search: search.value,
    };

    if (isSuperAdmin.value) {
        params.company_id = companyId.value;
    }

    Inertia.get('/management/providers', params, { preserveState: true, replace: true });
}, 500);

watch([search, companyId], () => updateSearch());

// ── Edit actions ───────────────────────────────────────────────────────
const openEdit = (provider) => {
    Inertia.get(
        '/management/providers',
        { provider: provider.uuid },
        { preserveState: true, preserveScroll: true }
    );
};

const backToList = () => {
    Inertia.get('/management/providers', {}, { preserveState: false });
};

// ── Helpers ────────────────────────────────────────────────────────────
const getStatusSeverity = (isActive) => isActive ? 'success' : 'danger';

const getTypeLabel = (type) => {
    const labels = { person: 'Mitarbeiter', team: 'Team', virtual: 'Virtuell' };
    return labels[type] || type;
};

const getTypeSeverity = (type) => {
    const severities = { person: 'info', team: 'success', virtual: 'warning' };
    return severities[type] || 'info';
};

const confirmDelete = (provider) => {
    confirm.require({
        message: `Sind Sie sicher, dass Sie den Mitarbeiter "${provider.name}" löschen möchten?`,
        header: 'Löschen bestätigen',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        acceptLabel: 'Löschen',
        rejectLabel: 'Abbrechen',
        accept: () => {
            Inertia.delete(`/management/providers/${provider.uuid}`, {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    toast.add({
                        severity: 'success',
                        summary: 'Erfolgreich',
                        detail: 'Mitarbeiter wurde gelöscht.',
                        life: 5000,
                    });
                }
            });
        }
    });
};
</script>

<template>
    <Layout :flash="flash">
        <Head title="Mitarbeiter" />

        <div class="space-y-6">
            <ManagementHeader title="Mitarbeiter Management" module="providers" :activeIndex="0">
                <template #actions>
                    <!-- + Add button: only in list mode -->
                    <Link v-if="mode === 'list'" href="/management/providers/create">
                        <Button label="Hinzufügen" icon="pi pi-plus" severity="success" class="!px-4 !py-2" />
                    </Link>
                </template>
            </ManagementHeader>

            <!-- ── LIST MODE ───────────────────────────────────────────────── -->
            <div v-if="mode === 'list'"
                 class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

                <!-- Search + company filter -->
                <div class="p-4 border-b border-gray-50 flex gap-4 bg-gray-50/50">
                    <IconField iconPosition="left" class="flex-1 max-w-md">
                        <InputIcon class="pi pi-search"></InputIcon>
                        <InputText v-model="search"
                                   class="w-full"
                                   placeholder="Suche nach Name oder Email..." />
                    </IconField>

                    <Dropdown v-if="isSuperAdmin"
                              v-model="companyId"
                              :options="companies"
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Alle Unternehmen"
                              class="w-64"
                              showClear />
                </div>

                <DataTable :value="providers.data"
                           :rows="20"
                           dataKey="id"
                           responsiveLayout="scroll"
                           class="p-datatable-sm">
                    <Column field="name"          header="Name"   sortable class="font-semibold"></Column>
                    <Column field="email"         header="Email"  sortable></Column>
                    <Column field="sort_order"    header="Sortierung" sortable></Column>
                    <Column field="provider_type" header="Typ"    sortable>
                        <template #body="slotProps">
                            <Tag :value="getTypeLabel(slotProps.data.provider_type)"
                                 :severity="getTypeSeverity(slotProps.data.provider_type)" />
                        </template>
                    </Column>
                    <Column field="is_active" header="Status" sortable>
                        <template #body="slotProps">
                            <Tag :value="slotProps.data.is_active ? 'Aktiv' : 'Inaktiv'"
                                 :severity="getStatusSeverity(slotProps.data.is_active)" />
                        </template>
                    </Column>
                    <Column header="Aktionen"
                            headerStyle="width: 8rem; text-align: center"
                            bodyStyle="text-align: center">
                        <template #body="slotProps">
                            <div class="flex justify-center gap-3">
                                <!-- Edit: opens inline form instead of navigating away -->
                                <Button icon="pi pi-pencil"
                                        severity="info"
                                        outlined
                                        class="!p-2"
                                        @click.stop="openEdit(slotProps.data)" />
                                <Button icon="pi pi-trash"
                                        severity="danger"
                                        outlined
                                        class="!p-2"
                                        @click.stop="confirmDelete(slotProps.data)" />
                            </div>
                        </template>
                    </Column>

                    <template #empty>
                        <div class="py-12 text-center">
                            <i class="pi pi-users text-4xl text-gray-200 mb-4" />
                            <p class="text-gray-400">Keine Mitarbeiter gefunden.</p>
                        </div>
                    </template>
                </DataTable>

                <!-- Pagination -->
                <div class="p-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center text-sm text-gray-500">
                    <div>
                        Zeige {{ providers.from }} bis {{ providers.to }} von {{ providers.total }} Einträgen
                    </div>
                    <div class="flex gap-2">
                        <Link v-for="link in providers.links"
                              :key="link.label"
                              :href="link.url || '#'"
                              v-html="link.label"
                              :class="[
                                'px-3 py-1 rounded border transition-colors',
                                link.active ? 'bg-black text-[#FFF053] border-black' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
                                !link.url ? 'opacity-50 cursor-not-allowed' : ''
                              ]" />
                    </div>
                </div>
            </div>

            <!-- ── EDIT MODE ───────────────────────────────────────────────── -->
            <div v-else-if="mode === 'edit' && editingProvider"
                 class="bg-white rounded-xl shadow-sm border border-gray-100 p-8">

                <!-- Header: back button + provider name -->
                <div class="flex items-center gap-4 mb-6">
                    <button type="button"
                            class="p-2 rounded-full hover:bg-gray-100 transition-colors"
                            @click="backToList">
                        <i class="pi pi-arrow-left text-gray-500" />
                    </button>
                    <h2 class="text-2xl font-bold text-gray-800">
                        Mitarbeiter bearbeiten:
                        <span class="text-gray-500 font-normal">{{ editingProvider.name }}</span>
                    </h2>
                </div>

                <ProviderEditForm
                    :provider="editingProvider"
                    :companies="companies"
                    :services="services"
                    :showBackButton="true"
                    @saved="backToList"
                    @cancel="backToList"
                />
            </div>
        </div>
    </Layout>
</template>

<style scoped>
:deep(.p-datatable-thead > tr > th) {
    background: transparent;
    color: #64748b;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 1rem;
}

:deep(.p-datatable-tbody > tr > td) {
    padding: 1rem;
    border-bottom: 1px solid #f8fafc;
}

:deep(.p-datatable-tbody > tr:hover) {
    background-color: #fcfcfd;
}
</style>
